/**
 * The external-compute producer + the single async orchestration edge
 * (Compute Integration, Agent 7 · Deliverables A + B + D).
 *
 * A `ComputeResource` is an async, side-effecting external engine; it lives HERE
 * at the L6 edge and is NEVER called by the kernel/reducer. Its result is recorded
 * as an immutable `ComputationRecord` fact and replayed deterministically — the
 * core stays a pure function of (claim, config) (INV-CORE / INV-DET).
 *
 * INV-DEP / F7: this module imports `app` + `contracts` (+ `view` for the headless
 * re-render) ONLY — never `@progression/core`. The producer is structurally unable
 * to reach the kernel; it can only emit a fact through the published
 * `record-computation` command.
 */
import { buildViewModel } from '@progression/view';
/** Default-deny check (F8): refuse a producer that asks for fs/network. */
export function assertSandboxed(resource) {
    if (resource.capabilities.filesystem || resource.capabilities.network) {
        throw new Error(`compute: resource "${resource.id}" requests fs/network capability; refused (F8 default-deny)`);
    }
}
/** Thrown when an engine cannot run in this environment — degrade cleanly, never crash. */
export class ComputeUnavailableError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ComputeUnavailableError';
    }
}
// ── The deterministic NumPy-shaped mock producer ─────────────────────────────
function asMatrix(inputs) {
    const m = inputs['matrix'];
    if (!Array.isArray(m))
        throw new Error('compute: inputs.matrix must be a 2D number array');
    return m;
}
/** Closed-form symmetric-2×2 eigenvalues (ascending) — a faithful `linalg.eigvalsh`. */
function eigvalsh2x2(matrix) {
    const r0 = matrix[0];
    const r1 = matrix[1];
    if (r0 === undefined || r1 === undefined || r0.length !== 2 || r1.length !== 2) {
        throw new Error('compute(mock): eigvalsh supports a 2×2 symmetric matrix');
    }
    const a = r0[0] ?? 0;
    const b = r0[1] ?? 0;
    const d = r1[1] ?? 0;
    const tr = a + d;
    const disc = Math.sqrt(Math.max(0, (tr * tr) / 4 - (a * d - b * b)));
    return [tr / 2 - disc, tr / 2 + disc];
}
function roundTo(x, decimals) {
    return Number(x.toFixed(decimals));
}
/** Evaluate the supported predicates over an eigenvalue spectrum. */
function evaluatePredicate(predicate, eigs) {
    const TOL = 1e-9;
    switch (predicate) {
        case 'all-nonnegative':
        case 'positive-semidefinite': {
            const refuted = eigs.some((x) => x < -TOL);
            return { held: !refuted, refutedProof: refuted };
        }
        case 'positive-definite': {
            const refuted = eigs.some((x) => x <= TOL);
            return { held: !refuted, refutedProof: refuted };
        }
        default:
            throw new Error(`compute(mock): unsupported predicate "${predicate}"`);
    }
}
const MOCK_ENGINE_VERSION = 'numpy-mock-1.26';
/**
 * A deterministic, serializable NumPy-shaped producer (no Pyodide needed). Pure
 * math only — touches no filesystem or network (F8). Used as the default and by
 * the deterministic tests; `createPyodideNumpyResource` is the real swap-in.
 */
export function createMockNumpyResource(isolation = 'BEST-EFFORT') {
    return {
        id: 'numpy',
        isolation,
        capabilities: { filesystem: false, network: false },
        run(req) {
            const decimals = 6;
            if (req.op !== 'linalg.eigvalsh') {
                return Promise.reject(new Error(`compute(mock): unsupported op "${req.op}"`));
            }
            const eigs = eigvalsh2x2(asMatrix(req.inputs)).map((x) => roundTo(x, decimals));
            const { held, refutedProof } = evaluatePredicate(req.predicate, eigs);
            return Promise.resolve({
                result: eigs,
                held,
                refutedProof,
                engineVersion: MOCK_ENGINE_VERSION,
                precision: { decimals },
            });
        },
    };
}
let _pyodidePromise = null;
async function getPyodide() {
    if (_pyodidePromise)
        return _pyodidePromise;
    _pyodidePromise = (async () => {
        let mod;
        try {
            const moduleName = 'pyodide';
            mod = (await import(/* @vite-ignore */ moduleName));
        }
        catch {
            _pyodidePromise = null; // allow retry in case of transient error
            throw new ComputeUnavailableError('Pyodide is not available in this environment');
        }
        if (typeof mod.loadPyodide !== 'function') {
            _pyodidePromise = null;
            throw new ComputeUnavailableError('Pyodide module present but loadPyodide missing');
        }
        let instance;
        try {
            instance = await mod.loadPyodide();
            await instance.loadPackage('numpy');
        }
        catch (err) {
            _pyodidePromise = null;
            throw new ComputeUnavailableError(`Pyodide failed to initialise: ${err.message}`);
        }
        return instance;
    })();
    return _pyodidePromise;
}
/**
 * The REAL producer: loads Pyodide + NumPy at run time. In an environment without
 * Pyodide (this sandbox, or a Worker without Python) it throws
 * `ComputeUnavailableError`, which the orchestration treats as a clean
 * "unavailable" (records nothing). Behind the same interface as the mock — a second
 * engine (octave/sympy) drops in the same way.
 *
 * Supports arbitrary N×N symmetric matrices (numpy.linalg.eigvalsh), unlike the
 * closed-form 2×2 mock. The matrix is passed via JSON — never string-interpolated
 * into Python source — so no injection surface exists (F8).
 */
export function createPyodideNumpyResource(isolation = 'BEST-EFFORT') {
    return {
        id: 'numpy',
        isolation,
        capabilities: { filesystem: false, network: false }, // Pyodide WASM sandbox: no fs/network (F7/F8)
        async run(req) {
            if (req.op !== 'linalg.eigvalsh') {
                throw new Error(`compute(pyodide): unsupported op "${req.op}"`);
            }
            const matrix = asMatrix(req.inputs);
            const decimals = 6;
            const pyodide = await getPyodide(); // throws ComputeUnavailableError if absent
            // Pass matrix via JSON — never interpolated into Python source.
            pyodide.globals.set('_matrix_json', JSON.stringify(matrix));
            const resultJson = String(await pyodide.runPythonAsync(`
import json, numpy as np
_eigs = np.linalg.eigvalsh(np.array(json.loads(_matrix_json), dtype=float)).tolist()
numpy_version = np.__version__
json.dumps({'eigs': _eigs, 'numpy_version': numpy_version})
`));
            const parsed = JSON.parse(resultJson);
            const eigs = parsed.eigs.map((x) => roundTo(x, decimals));
            const engineVersion = `numpy-${parsed.numpy_version}+pyodide-${pyodide.version}`;
            const { held, refutedProof } = evaluatePredicate(req.predicate, eigs);
            return { result: eigs, held, refutedProof, engineVersion, precision: { decimals } };
        },
    };
}
/**
 * The §3 flow — the ONLY new async control flow. The pure core/reducer stay
 * synchronous; a failed/unavailable/cancelled run records nothing (store untouched).
 */
export async function runComputation(store, resource, spec, options = {}) {
    if (options.signal?.aborted)
        return { status: 'cancelled' };
    // no_equivocation: bind the result to the kernel config it was derived under.
    const configDigest = store.verdictOf(spec.claimRef).configDigest;
    const request = {
        engine: resource.id,
        op: spec.op,
        inputs: spec.inputs,
        predicate: spec.predicate,
        configDigest,
    };
    let result;
    try {
        result = await resource.run(request);
    }
    catch (err) {
        if (err instanceof ComputeUnavailableError)
            return { status: 'unavailable', reason: err.message };
        return { status: 'failed', reason: String(err.message) }; // store untouched
    }
    if (options.signal?.aborted)
        return { status: 'cancelled' }; // record nothing
    const record = {
        id: spec.recordId,
        claimRef: spec.claimRef,
        engine: resource.id,
        engineVersion: result.engineVersion,
        op: spec.op,
        inputLabelId: spec.inputLabelId,
        result: result.result,
        predicateId: spec.predicateId,
        held: result.held,
        refutedProof: result.refutedProof,
        configDigest,
        recordedAtTick: store.exportLog().length, // logical tick — deterministic, never Date.now()
    };
    const recordCmd = { c: 'record-computation', record };
    store.dispatch(recordCmd);
    // If the predicate held, activate the verified base so the REAL kernel re-derives.
    if (result.held && spec.verifiedBase !== undefined) {
        const activateCmd = { c: 'activate', id: spec.verifiedBase };
        store.dispatch(activateCmd);
    }
    // Re-render headless from the (re-derived) state.
    const vm = buildViewModel(store.getState(), options.register ?? 'full');
    options.onRender?.(vm);
    return { status: 'recorded', record, held: result.held };
}
//# sourceMappingURL=compute.js.map